import config from "../../config/config";
import { apiCall } from "../apiCall";

/**
 * Frontend service for the assessment flow on company_admin. All calls go
 * through the company-admin-backend (NestJS), which proxies to riu_backend.
 */
const BASE = `${config.POSTLOGIN_API_URL_COMPANY}assessment`;
const KPI_BASE = `${config.POSTLOGIN_API_URL_COMPANY}kpi`;

export const AssessmentService = {
  listAssessments,
  getAssessment,
  createAssessment,
  updateAssessment,
  deleteAssessment,

  assignAssessment,
  listAssignments,
  listMyAssignments,
  listBySupplier,
  getBySupplier,
  getByAssignment,
  updateAssignmentStatus,
  cancelAssignment,
  submitAssignment,
  upsertAssignmentResponse,
  listAssignmentResponses,

  listAssignmentDelegates,
  addAssignmentDelegates,
  removeAssignmentDelegate,
  listSupplierUsers,
  listUserDelegations,

  listSuppliers,
  listIndustries,
  listKpis,

  listRiuTemplates,
  getRiuTemplateKpis,

  listCompanyKpis,
  getCompanyKpi,
  createCompanyKpi,
  updateCompanyKpi,
  deleteCompanyKpi,
};

async function listAssessments(params = {}) {
  return apiCall(BASE, {}, params, "GET");
}

/**
 * @param {number|string} id
 * @param {'company'|'riu_backend'} [source] defaults to 'company' server-side
 */
async function getAssessment(id, source) {
  return apiCall(`${BASE}/${id}`, {}, source ? { source } : {}, "GET");
}

async function createAssessment(payload) {
  return apiCall(BASE, {}, payload, "POST");
}

async function updateAssessment(id, payload, source) {
  const url = source ? `${BASE}/${id}?source=${source}` : `${BASE}/${id}`;
  return apiCall(url, {}, payload, "PUT");
}

/** ?source=riu_backend returns 403; DELETE only makes sense for company-owned assessments. */
async function deleteAssessment(id, source) {
  const url = source ? `${BASE}/${id}?source=${source}` : `${BASE}/${id}`;
  return apiCall(url, {}, {}, "DELETE");
}

/**
 * @param {{ supplier_id: number, assessment_id: number, source?: 'company'|'riu_backend', due_at?: string }} payload
 */
async function assignAssessment(payload) {
  return apiCall(`${BASE}/assign`, {}, payload, "POST");
}

async function listAssignments(params = {}) {
  return apiCall(`${BASE}/assignments/list`, {}, params, "GET");
}

/** Supplier-facing: assignments where the logged-in user IS the supplier. */
async function listMyAssignments(params = {}) {
  return apiCall(`${BASE}/my-assignments`, {}, params, "GET");
}

/**
 * Per-supplier drill-in used by /assessment/active/:supplierId and
 * /assessment/past/:supplierId. Rows have the same shape as
 * listAssignments so client-side status filtering (ACTIVE vs PAST)
 * still works.
 */
async function listBySupplier(supplierId, params = {}) {
  return apiCall(`${BASE}/by-supplier/${supplierId}`, {}, params, "GET");
}

/**
 * Fetch a single assessment as seen by a specific supplier — response
 * carries the assignment context (status_key, due_at, etc.) so the view
 * page can surface the ASSIGNMENT status instead of the assessment
 * lifecycle status.
 */
async function getBySupplier(supplierId, id, source) {
  return apiCall(
    `${BASE}/by-supplier/${supplierId}/${id}`,
    {},
    source ? { source } : {},
    "GET"
  );
}

/**
 * Fetch an assignment plus its assessment by assignment id. Backing the
 * clean path-based route /assessment/assignment/:assignmentId (and /take),
 * where the assignment id alone identifies supplier + assessment + status.
 */
async function getByAssignment(assignmentId) {
  return apiCall(`${BASE}/by-assignment/${assignmentId}`, {}, {}, "GET");
}

async function updateAssignmentStatus(id, status) {
  return apiCall(
    `${BASE}/assignments/${id}/status`,
    {},
    { status },
    "PUT",
    false,
    false
  );
}

/**
 * Company-side terminal action: cancel an assignment previously sent to a
 * supplier. The backend transitions to CANCELLED from any non-terminal
 * status and emails the supplier. Only the assigning company may call
 * this endpoint.
 */
async function cancelAssignment(id) {
  return apiCall(
    `${BASE}/assignments/${id}/cancel`,
    {},
    {},
    "PATCH"
  );
}

/**
 * Upsert a single KPI answer on an assignment. Server enforces
 * "answer parent first" (409) and terminal-state edits (409). The `answer`
 * value is stored as JSON so a string, number, object, or array all work.
 */
async function upsertAssignmentResponse(assignmentId, payload) {
  return apiCall(
    `${BASE}/assignments/${assignmentId}/response`,
    {},
    payload,
    "PUT",
    false,
    false
  );
}

async function listAssignmentResponses(assignmentId) {
  return apiCall(
    `${BASE}/assignments/${assignmentId}/responses`,
    {},
    {},
    "GET"
  );
}

async function listAssignmentDelegates(assignmentId) {
  return apiCall(
    `${BASE}/assignments/${assignmentId}/delegates`,
    {},
    {},
    "GET"
  );
}

async function addAssignmentDelegates(assignmentId, userIds) {
  return apiCall(
    `${BASE}/assignments/${assignmentId}/delegates`,
    {},
    { user_ids: userIds },
    "POST"
  );
}

async function removeAssignmentDelegate(assignmentId, userId) {
  return apiCall(
    `${BASE}/assignments/${assignmentId}/delegates/${userId}`,
    {},
    {},
    "DELETE"
  );
}

async function listSupplierUsers() {
  return apiCall(
    `${config.POSTLOGIN_API_URL_COMPANY}supplier/users`,
    {},
    {},
    "GET"
  );
}

async function listUserDelegations(userId) {
  return apiCall(
    `${BASE}/users/${userId}/delegations`,
    {},
    {},
    "GET"
  );
}

/**
 * Supplier-side terminal action: mark an assignment as SUBMITTED. Thin
 * wrapper over updateAssignmentStatus so call sites can read clearly and
 * the backend stays free to add a dedicated submit route later without
 * touching consumers.
 */
async function submitAssignment(id) {
  return updateAssignmentStatus(id, "SUBMITTED");
}

async function listSuppliers() {
  return apiCall(`${BASE}/suppliers`, {}, {}, "GET");
}

async function listIndustries() {
  return apiCall(
    `${config.POSTLOGIN_API_URL_COMPANY.replace("/postLogin/", "/preLogin/")}suppliers/industries`,
    {},
    {},
    "GET"
  );
}

/**
 * Resolve the current financial year id from the shared `financialYearData`
 * bucket in localStorage (same source the sidebar seeds). Falls back to
 * `null` when the bucket is empty; callers must handle that (no request is
 * issued without an id).
 */
function currentFinancialYearId() {
  try {
    const raw = localStorage.getItem("financialYearData");
    if (!raw) return null;
    const list = JSON.parse(raw);
    if (!Array.isArray(list) || !list.length) return null;
    return list[list.length - 1]?.id ?? null;
  } catch {
    return null;
  }
}

/**
 * Pull the KPI catalog and pass through everything the picker's filters
 * need (raw cascade tags, kpi_status for the status filter, the audit
 * timestamps for the date filter, moduleName/title for search) instead
 * of stripping down to `{ id, title }`.
 */
async function listKpis() {
  const financialYearId = currentFinancialYearId();
  if (!financialYearId) {
    return { isSuccess: false, data: { data: [] } };
  }
  const res = await apiCall(
    `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestion`,
    {},
    { financialYearId },
    "GET"
  );
  const rows = res?.data?.data ?? [];
  const normalised = Array.isArray(rows)
    ? rows.map((r) => ({
        id: r.questionId,
        title: r.title || r.kpi_name || "(untitled)",
        moduleName: r.moduleName ?? null,
        kpi_status: r.kpi_status ?? null,
        kpi_type: r.kpi_type ?? null,
        createdAt: r.createdAt ?? null,
        updatedAt: r.updatedAt ?? null,
        rawTags: r.tags ?? null,
      }))
    : [];
  return { ...res, data: { ...(res?.data || {}), data: normalised } };
}

/** RIU-provided assessment templates, used as the "starting point" library in the composer. */
async function listRiuTemplates(params = {}) {
  return apiCall(BASE, {}, { ...params, owner_type: "RIU_TEMPLATE" }, "GET");
}

/** Fetch a template's full KPI list so the composer can show them for picking. */
async function getRiuTemplateKpis(id) {
  return apiCall(`${BASE}/${id}`, {}, { source: "riu_backend" }, "GET");
}

/**
 * List the company's own KPI bank.
 * @param {{ search?: string, question_type?: string, frequency?: string, kpi_status?: string, scope?: 'active'|'archived'|'all', limit?: number, offset?: number }} [params]
 */
async function listCompanyKpis(params = {}) {
  return apiCall(KPI_BASE, {}, params, "GET");
}

async function getCompanyKpi(id) {
  return apiCall(`${KPI_BASE}/${id}`, {}, {}, "GET");
}

async function createCompanyKpi(payload) {
  return apiCall(KPI_BASE, {}, payload, "POST");
}

async function updateCompanyKpi(id, payload) {
  return apiCall(`${KPI_BASE}/${id}`, {}, payload, "PUT");
}

/**
 * Soft-delete a company KPI. On 409, the response payload carries
 * `assessment_ids: number[]` — those assessments must be un-attached first.
 * The caller should read `response.errorBody?.assessment_ids` when
 * `response.status === 409`.
 */
async function deleteCompanyKpi(id) {
  return apiCall(`${KPI_BASE}/${id}`, {}, {}, "DELETE", true, false);
}
